import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import type { Game, SportGame } from "./GamesProvider";

const toCssVariable = (key: string): string => {
  const baseKey = key.endsWith("Color") ? key.slice(0, -5) : key;
  return `--color-${baseKey.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
};

const generateColorVariants = (color: string) => {
  return {
    "100": color,
    "80": adjustOpacity(color, 0.8),
    "60": adjustOpacity(color, 0.6),
    "40": adjustOpacity(color, 0.4),
    "20": adjustOpacity(color, 0.2),
    "10": adjustOpacity(color, 0.1),
    "5": adjustOpacity(color, 0.05),
  };
};

const adjustOpacity = (color: string, opacity: number): string => {
  let r, g, b;
  if (color?.startsWith("#")) {
    const hex = color.replace("#", "");
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    const rgb = color.match(/\d+/g)?.map(Number);
    if (!rgb) return color;
    [r, g, b] = rgb;
  }

  const a = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0");

  const hex = [r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("");

  return `#${hex}${a}`;
};

export const setPromoColors = (colors: Record<string, string>) => {
  const root = document.documentElement;

  Object.entries(colors).forEach(([key, value]) => {
    if (typeof value !== "string" || !value?.startsWith("#")) {
      return;
    }

    const cssVarBase = toCssVariable(key);
    const variants = generateColorVariants(value);

    Object.entries(variants).forEach(([opacity, color]) => {
      const varName = opacity === "100" ? cssVarBase : `${cssVarBase}-${opacity}`;
      root.style.setProperty(varName, color);
    });

    if (key.toLowerCase().includes("toast")) {
      const toastVar = `--color-${key.toLowerCase()}`;
      root.style.setProperty(toastVar, value);
    }
  });
};

export const useQueryWithNoCache = (
  queryKey: unknown[],
  url: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    select?: (data: any) => any;
    [key: string]: any;
  }
) => {
  return useQuery({
    queryKey,
    queryFn: () =>
      axios.get(url, {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }),
    ...options,
  });
};

const withCacheBuster = (url: string) => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}_=${Date.now()}`;
};

const fetchCdnJson = async (url: string, shouldBustCache = false) => {
  const response = await fetch(shouldBustCache ? withCacheBuster(url) : url, {
    cache: "no-store",
    credentials: "omit",
    mode: "cors",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch CDN JSON: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

const useCdnJsonQuery = (
  queryKey: unknown[],
  url: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    select?: (data: any) => any;
    cacheBust?: boolean;
    [key: string]: any;
  }
) => {
  const { cacheBust, ...queryOptions } = options || {};

  return useQuery({
    queryKey,
    queryFn: () => fetchCdnJson(url, cacheBust),
    retry: false,
    ...queryOptions,
  });
};

const deserializeGame = (res: any): Game => {
  res.id = String(res?.i ?? res?.id ?? "");
  res.extGameId = String(res?.eid ?? res?.extGameId ?? "");
  res.name = res?.n || res?.name;
  res.providerName = res?.p || res?.providerName;
  res.extProviderId = res?.epid || res?.extProviderId || "";
  res.providerDisplayName = res?.pn || res?.providerDisplayName || "";
  res.isLive = res?.l ?? res?.isLive;
  res.displayName = res?.dn || res?.displayName;
  res.image = res?.img || res?.image || null;
  res.link = res?.lk || res?.link || null;
  res.category = res?.c || res?.category;

  delete res.i;
  delete res.eid;
  delete res.n;
  delete res.l;
  delete res.p;
  delete res.pn;
  delete res.dn;
  delete res.epid;
  delete res.img;
  delete res.lk;
  delete res.c;

  return res;
};

const deserializeSportGame = (res: any): SportGame => {
  const sportType = res?.st || res?.sportType || "";
  const tournament = res?.t || res?.tournament || "";

  res.id = String(res?.i || res?.id || `${sportType}:${tournament}`);
  res.sportType = sportType;
  res.sportDisplayName = res?.dn || res?.displayName || sportType;
  res.tournament = tournament;
  res.tournamentDisplayName = res?.tdn || res?.tournamentDisplayName || tournament;
  res.image = res?.img || res?.image || null;
  res.link = res?.lk || res?.link || null;
  res.isLive = res?.l ?? res?.isLive;

  delete res.i;
  delete res.st;
  delete res.dn;
  delete res.t;
  delete res.tdn;
  delete res.img;
  delete res.lk;
  delete res.l;

  return res;
};

const deserializeGames = (data: any[] = []): Game[] => {
  data.forEach(deserializeGame);
  return data;
};

const deserializeSportGames = (data: any[] = []): SportGame[] => {
  data.forEach(deserializeSportGame);
  return data;
};

export const useGamesQuery = (orgUuid: string | undefined) => {
  const url = `${import.meta.env.VITE_WIDGET_CDN_BASE_URL}/organization/${orgUuid}/casino/games/games.json`;

  return useCdnJsonQuery(["games", orgUuid], url, {
    select: deserializeGames,
    enabled: !!orgUuid,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSportGamesQuery = (orgUuid: string | undefined) => {
  const url = `${import.meta.env.VITE_WIDGET_CDN_BASE_URL}/organization/${orgUuid}/casino/games/sport_games.json`;

  return useCdnJsonQuery(["sportGames", orgUuid], url, {
    select: deserializeSportGames,
    enabled: !!orgUuid,
    staleTime: 5 * 60 * 1000,
    cacheBust: true,
  });
};
